type ToastType = 'success' | 'error' | 'info';

interface ToastOptions {
    message: string;
    type: ToastType;
    delay?: number;
}

class Toast {
    private element: HTMLDivElement;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private dismissCookieName: string | null = null;

    constructor(message: string, type: ToastType = 'success', delay: number = 5000, dismissCookieName: string | null = null) {
        this.dismissCookieName = dismissCookieName;
        this.element = this.createToastElement(message, type);
        this.attachCloseHandler();
        this.show();
        if (delay > 0) {
            this.timer = setTimeout(() => {
                this.remove();
            }, delay);
        }
    }

    private createToastElement(message: string, type: ToastType): HTMLDivElement {
        const container = this.getOrCreateContainer();

        const toast = document.createElement('div');
        toast.classList.add('toast-save');
        toast.classList.add(this.getClassForType(type));
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'assertive');

        const icon = document.createElement('span');
        icon.classList.add('toast-save-icon');
        icon.innerHTML = this.getIconForType(type);

        const content = document.createElement('span');
        content.classList.add('toast-save-content');
        content.innerHTML = message;

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.classList.add('toast-save-close');
        closeBtn.setAttribute('aria-label', 'Schließen');
        closeBtn.innerHTML = '&times;';

        toast.appendChild(icon);
        toast.appendChild(content);
        toast.appendChild(closeBtn);

        container.appendChild(toast);
        return toast;
    }

    private getOrCreateContainer(): HTMLDivElement {
        let container = document.getElementById('toast-container') as HTMLDivElement | null;
        if (!container) {
            container = document.createElement('div');
            container.id = 'toast-container';
            document.body.appendChild(container);
        }
        return container;
    }

    private getClassForType(type: ToastType): string {
        switch (type) {
            case 'success': return 'toast-success';
            case 'error': return 'toast-error';
            case 'info': return 'toast-info';
        }
    }

    private getIconForType(type: ToastType): string {
        switch (type) {
            case 'success': return '✓';
            case 'error': return '✗';
            case 'info': return 'ℹ';
        }
    }

    private attachCloseHandler(): void {
        const closeBtn = this.element.querySelector('.toast-save-close');
        if (closeBtn) {
            closeBtn.addEventListener('click', () => {
                this.remove();
            });
        }
    }

    private show(): void {
        requestAnimationFrame(() => {
            this.element.classList.add('toast-show');
        });
    }

    remove(): void {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (this.dismissCookieName) {
            document.cookie = this.dismissCookieName + '=1';
        }
        this.element.classList.remove('toast-show');
        setTimeout(() => {
            if (this.element.parentNode) {
                this.element.parentNode.removeChild(this.element);
            }
        }, 300);
    }
}

export function showToast(message: string, type: ToastType = 'success', delay: number = 5000, dismissCookieName: string | null = null): void {
    new Toast(message, type, delay, dismissCookieName);
}

// Make showToast globally available on window
(window as any).showToast = showToast;

// Process flash messages immediately if DOM is ready, or wait for load
if (document.readyState === 'complete' || document.readyState === 'interactive') {
    // DOM already loaded, process immediately after a tiny delay
    setTimeout(() => {
        processFlashMessages();
    }, 0);
} else {
    // DOM not ready yet, process when it loads
    document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
            processFlashMessages();
        }, 0);
    });
}

export function processFlashMessages(): void {
    const flashMessages: Array<{ message: string, type: ToastType, dismissCookieName: string | null }> = [];

    const successFlash = document.getElementById('flash-success');
    const errorFlash = document.getElementById('flash-error');
    const infoFlash = document.getElementById('flash-info');

    if (successFlash) {
        readFlashes(successFlash, 'success', flashMessages);
    }
    if (errorFlash) {
        readFlashes(errorFlash, 'error', flashMessages);
    }
    if (infoFlash) {
        readFlashes(infoFlash, 'info', flashMessages);
    }

    flashMessages.forEach((msg, idx) => {
        setTimeout(() => {
            showToast(msg.message, msg.type, 5000, msg.dismissCookieName);
        }, idx * 500);
    });
}

function readFlashes(
    container: HTMLElement,
    type: ToastType,
    messages: Array<{ message: string, type: ToastType, dismissCookieName: string | null }>
): void {
    const items = container.querySelectorAll('[data-flash-message]');
    items.forEach((item) => {
        const message = item.getAttribute('data-flash-message');
        if (message) {
            const dismissCookieName = message.includes('Zertifikatsnummer') || message.includes('Selbsterklärung')
                ? 'selfDeclarationExpiration'
                : null;
            messages.push({ message, type, dismissCookieName });
        }
    });
}
